import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useShows, useCurrentUser } from '../api/hooks'
import { useCurrentShow } from '@/context/current-show'
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { TShow } from '@schemas'
import { STATUS_LABELS } from '@/lib/show-status'
import { parseApiErrorStatus } from '@/lib/api-errors'
import { formatDateRangeDateOnly, parseDateOnlyToLocal } from '@/lib/date'

const upcomingQueryParams = {
  when: 'upcoming' as const,
  orderBy: 'startDate:asc' as const,
  limit: 20,
}
const acceptingEntriesQueryParams = {
  status: 'accepting_entries' as const,
  orderBy: 'startDate:asc' as const,
}
const judgingQueryParams = { status: 'judging' as const, orderBy: 'startDate:asc' as const }
const STATUS_ORDER: TShow['status'][] = ['draft', 'accepting_entries', 'judging', 'finalized']

export default function LandingPage() {
  const user = useCurrentUser()
  const isAdmin = !user.isLoading && user.data?.isAdmin === true
  const navigate = useNavigate()
  const { setCurrentShow } = useCurrentShow()

  const acceptingEntriesShows = useShows(acceptingEntriesQueryParams)
  const judgingShows = useShows(judgingQueryParams)
  const upcomingShows = useShows(upcomingQueryParams)

  const inProgressShows = useMemo(() => {
    const combined: TShow[] = [...(acceptingEntriesShows.data ?? []), ...(judgingShows.data ?? [])]

    if (combined.length === 0) return []

    return combined.sort((a, b) => {
      const statusDelta = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
      if (statusDelta !== 0) return statusDelta

      const aDate = parseDateOnlyToLocal(a.startDate)
      const bDate = parseDateOnlyToLocal(b.startDate)
      if (aDate && bDate && aDate.getTime() !== bDate.getTime()) {
        return aDate.getTime() - bDate.getTime()
      }
      if (aDate && !bDate) return -1
      if (!aDate && bDate) return 1

      return (a.name ?? '').localeCompare(b.name ?? '')
    })
  }, [acceptingEntriesShows.data, judgingShows.data])

  const inProgressLoading = acceptingEntriesShows.isLoading && judgingShows.isLoading

  // The server refuses the list (403) for an account with no show role. An empty list only means
  // none of the account's shows are upcoming.
  const needsAssignment = !isAdmin && parseApiErrorStatus(upcomingShows.error) === 403

  const resolveDestination = (show: TShow, destination: string) => {
    if (destination === '/run' || destination === '/setup') {
      return show.slug ? `/${show.slug}${destination}` : destination
    }
    return destination
  }

  const sendToShowContext = (show: TShow, destination: string) => {
    setCurrentShow(show)

    toast.success(`Working on “${show.name ?? 'this show'}”`)
    navigate(resolveDestination(show, destination))
  }

  const renderShowList = (shows: TShow[] | undefined, loading: boolean, emptyText: string) => {
    if (loading) {
      return (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map(key => (
            <Card key={key} className="p-4">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="mt-2 h-4 w-1/3" />
              <Skeleton className="mt-4 h-10 w-full" />
            </Card>
          ))}
        </div>
      )
    }

    if (!shows || shows.length === 0) {
      return <p className="text-sm text-muted-foreground">{emptyText}</p>
    }

    return (
      <div className="grid gap-4 md:grid-cols-2">
        {shows.map(show => {
          const domId = (show.id ?? show.name).replace(/[^a-z0-9_-]/gi, '-').toLowerCase()
          return (
            <Card key={show.id ?? show.name} data-testid={`show-card-${domId}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{show.name}</CardTitle>
                  <Badge
                    variant={
                      show.status === 'accepting_entries' || show.status === 'judging'
                        ? 'default'
                        : show.status === 'finalized'
                          ? 'secondary'
                          : 'outline'
                    }
                  >
                    {STATUS_LABELS[show.status] ?? show.status}
                  </Badge>
                </div>
                <CardDescription>
                  {formatDateRangeDateOnly(show.startDate, show.endDate)} •{' '}
                  {show.location ?? 'Location TBA'}
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex gap-2">
                <Button
                  size="sm"
                  data-testid={`run-show-${domId}`}
                  onClick={() => sendToShowContext(show, '/run')}
                >
                  Run Show
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid={`show-details-${domId}`}
                  onClick={() => sendToShowContext(show, '/setup')}
                >
                  Show Setup
                </Button>
              </CardFooter>
            </Card>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-10">
      <section className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Honey Show Control Center</h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? 'Choose a show to work on or create a new one.'
              : 'Choose a show assigned to you.'}{' '}
            Shows in Accepting entries or Judging phases surface here so your team can act quickly.
          </p>
          <div className="flex gap-4 text-sm">
            <Link to="/shows" className="text-primary underline underline-offset-4">
              View all shows
            </Link>
            {isAdmin && (
              <Link to="/organizations" className="text-primary underline underline-offset-4">
                View all organizations
              </Link>
            )}
          </div>
        </div>
        {isAdmin && (
          <Button size="lg" asChild>
            <Link to="/shows/new">Create New Show</Link>
          </Button>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">In-progress shows</h2>
            <p className="text-sm text-muted-foreground">
              Shows currently in the intake or judging phases.
            </p>
          </div>
        </div>
        {renderShowList(inProgressShows, inProgressLoading, 'No shows currently in progress.')}
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Upcoming shows</h2>
            <p className="text-sm text-muted-foreground">Recently scheduled shows starting soon.</p>
          </div>
          <Link to="/shows" className="text-sm text-primary underline underline-offset-4">
            View all shows
          </Link>
        </div>
        {renderShowList(
          upcomingShows.data,
          upcomingShows.isLoading,
          isAdmin
            ? 'No upcoming shows yet. Create one to get started!'
            : needsAssignment
              ? 'No upcoming shows yet. Ask an administrator or your show organizer to assign you to a show.'
              : 'No upcoming shows yet.'
        )}
      </section>
    </div>
  )
}
